import type { GlobalTranslations } from "@/shared/lang/interfaces/global";
import type { Permission } from "@/shared/types/permission.types";
import { UserType } from "@/shared/types/user.entity";

/**
 * THE navigation registry — one entry per navigable surface, with the requirement that makes it visible.
 *
 * It replaces the five overlapping authorization layers the dashboard used to run (a per-role path
 * allow-list, five ad-hoc scope filters, the organization module list, route guards, and per-view `can()`
 * calls). The point is not brevity: the sidebar and the router now read the SAME array, so a menu item and
 * its route can no longer disagree — which is how `/app/audit` ended up hidden in the sidebar and still
 * reachable by typing the URL.
 *
 * Adding a module means appending ONE entry here. Nothing else.
 *
 * The four axes are deliberately separate because they answer different questions and fail differently:
 *
 * - `userTypes` — TENANCY. Mirrors an actual `@JwtAuth({ userTypes: [...] })` on the endpoints the view
 *   consumes. Removing it would show a view the API answers with 403.
 * - `requires` — RBAC. ANY-of semantics, DEFAULT DENY. Mirrors `@JwtAuth({ permissions: [...] })`.
 * - `internalOnlyMenu` — PRODUCT. Curation only; the API would allow it. Reversible without a backend change,
 *   and named so nobody later mistakes it for a security boundary.
 * - `enabled` — FEATURE FLAG.
 *
 * Every `requires` / `userTypes` value below was read off the controller that serves the view, in
 * `coloca-nest` on `qa`. When a view's endpoint changes, this file changes with it.
 */

export const SECTION_IDS = ["operations", "home", "cards", "analytics", "management"] as const;

export type SectionId = (typeof SECTION_IDS)[number];

/** Where an entry renders. `route` = guarded destination with no menu item of its own. */
export type NavPlacement = "sidebar" | "footer" | "route";

export interface NavContext {
  userType: UserType;
  role: string | undefined;
  /** RBAC-effective permissions, from `user.scopes` (projected by the backend since `a7db964`). */
  permissions: ReadonlySet<Permission>;
  /**
   * `organization.allowed_menu_modules` as a set, or `null` when the organization does not restrict (the
   * default). Internal staff have no organization and always pass.
   */
  orgAllowedSections: ReadonlySet<string> | null;
  /** Feature flag: the "Pockets" card surface. */
  pocketsVisible: boolean;
  /** Feature flag: batch payouts. Staff always; members once staff enabled it for their organization. */
  payoutBatchesEnabled: boolean;
  /** Staff listed in the backend's WALLET_TOP_UP_ADMIN_EMAILS; everyone else never sees the surface. */
  walletTopUpsAllowed: boolean;
  /** Staff listed in the backend's METRICS_ADMIN_EMAILS; everyone else never sees the surface. */
  metricsAllowed: boolean;
  /** Feature flag: buy and sell USDT. Staff always; members once their organization (or all) has it on. */
  cryptoEnabled: boolean;
  /**
   * Feature flag: paying Bre-B QRs. Staff always; members unless staff switched it off for their
   * organization.
   */
  brebQrPaymentsEnabled: boolean;
}

export interface NavEntry {
  /** Navigation target. Unique — it identifies the entry. */
  to: string;
  /** Route pattern, for the router guard and the active-item highlight (e.g. `"wallets/*"`). */
  path: string;
  /**
   * Functional area. Drives the organization module allow-list on BOTH the sidebar and the route, so an org
   * restricted to `["home"]` cannot reach a management view by URL either. `null` = utility surface, never
   * restricted (profile, settings).
   */
  section: SectionId | null;
  placement: NavPlacement;
  /** I18n key in the `global` namespace. `null` for route-only entries, which render no label. */
  labelKey: keyof GlobalTranslations | null;
  /** RBAC requirement, ANY-of. Absent = the entry only needs a valid session. */
  requires?: Permission[];
  /** Tenancy. Set it only where the backend declares `userTypes` on the endpoints behind the view. */
  userTypes?: UserType[];
  /** Product curation for the menu. NOT security — say why in a comment every time it is used. */
  internalOnlyMenu?: boolean;
  enabled?: (ctx: NavContext) => boolean;
  /**
   * Extra route patterns that light this menu item up — for DETAIL surfaces that have no item of their own.
   *
   * Purely presentational: it decides which row is highlighted, never what anybody may open. A detail route
   * still needs its own `placement: "route"` entry to be authorized.
   *
   * Without it, a detail URL is claimed by the shallowest item whose `to` happens to prefix it: an order at
   * `/app/crypto-swaps/orders/<id>` would highlight "Buy USDT", the one surface it is NOT.
   *
   * **Never write a splat here.** `matchPath` strips it, so `"/app/x/*"` and `"/app/x"` report the same depth
   * and the tie puts us back where we started. `resolveNavAccess.test.ts` asserts this for the whole
   * registry.
   */
  activeFor?: string[];
  /**
   * Fold this entry into its section's collapsible group row instead of listing it at the section's own
   * level. See {@link NAV_MENU_GROUPS}.
   *
   * PRESENTATION ONLY. It changes no predicate in `resolveNavAccess`: the entry stays a unique destination
   * with its own `requires` / `userTypes`, `resolveSections` still returns it, and `resolveActiveNavTarget`
   * still resolves it to itself. Only where the row is DRAWN changes.
   */
  menuGroup?: NavMenuGroupId;
}

export const NAV_MENU_GROUP_IDS = ["crypto-swaps"] as const;

export type NavMenuGroupId = (typeof NAV_MENU_GROUP_IDS)[number];

/**
 * A collapsible row INSIDE a section, grouping several of its entries.
 *
 * A group is **not a navigable surface**, so it is deliberately not a `NavEntry`: it has no `to`, no
 * `requires` and no route. Inventing a synthetic entry for it would break four things at once — it would
 * become a landing candidate in `resolveLandingPath` (sending a user to a path with no route), it would join
 * the `path` group that `isRouteGroupAccessible` admits on ANY member (widening a route guard by an entry
 * nobody audited), and it would fail both the unique-`to` and the "every destination resolves to itself"
 * registry-integrity tests.
 *
 * What lives here is what a group IS: which section it belongs to and what it is called. The icon does not,
 * because this file imports no React — it is declared beside the section icons in `DashboardLayout`.
 */
export interface NavMenuGroup {
  id: NavMenuGroupId;
  section: SectionId;
  labelKey: keyof GlobalTranslations;
}

export const NAV_MENU_GROUPS: readonly NavMenuGroup[] = [
  // "Cripto" rather than "Transferencias cripto": a group row carries a chevron, which leaves 127px for the
  // label, and the longer string measures 140px in Inter 14 (the same in Portuguese) — it would ship
  // truncated. The compromise belongs on the row that is NOT a destination; the two children keep the
  // module's own words.
  { id: "crypto-swaps", section: "home", labelKey: "sidebar_menu_crypto_swaps_group" },
];

/**
 * Order matters: it is the order the sidebar renders. Sections follow {@link SECTION_IDS}; inside a section,
 * entries follow this array.
 */
const cryptoEnabled = (ctx: NavContext): boolean => ctx.userType === UserType.INTERNAL || ctx.cryptoEnabled;

export const NAVIGATION_REGISTRY: readonly NavEntry[] = [
  // ── operations ────────────────────────────────────────────────────────────────────────────────
  {
    to: "/app/confirmations",
    path: "confirmations/*",
    section: "operations",
    placement: "sidebar",
    labelKey: "sidebar_menu_confirmations",
    requires: ["operations:confirmations:read"],
    // confirmations.controller.ts — every route carries userTypes: [INTERNAL].
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payout-requests",
    path: "payout-requests/*",
    section: "operations",
    placement: "sidebar",
    labelKey: "sidebar_menu_payments",
    requires: ["operations:payments:read"],
    // operations/payments/payments.controller.ts — every route carries userTypes: [INTERNAL].
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payout-rescue",
    path: "payout-rescue/*",
    section: "operations",
    placement: "sidebar",
    labelKey: "sidebar_menu_payout_rescue",
    // Listing the stuck queue is `payments:read`; dispatch/cancel additionally demand `payments:write`
    // AND INTERNAL (payouts.internal.controller.ts:77,89). The menu gates on reading the queue — the
    // action buttons are the backend's call, latched into NoAccessView.
    requires: ["payments:read"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/swaps",
    path: "swaps/*",
    section: "operations",
    placement: "sidebar",
    labelKey: "sidebar_menu_swaps",
    requires: ["payments:read"],
    // PRODUCT, not tenancy. swaps.internal.controller.ts asks only for payments:read/write and declares
    // NO userTypes, and swaps.public.controller.ts lets an organization swap with its API key today. So
    // the API says yes and the menu says no — on purpose, and reversible here alone. Marking it
    // `userTypes: [INTERNAL]` would misinform the next reader into thinking the backend blocks it.
    internalOnlyMenu: true,
  },

  // ── home ──────────────────────────────────────────────────────────────────────────────────────
  {
    to: "/app/wallets",
    path: "wallets/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_wallets_home",
    // payment-wallets.internal.controller.ts:58 — payments:read, no userTypes (freeze/unfreeze are
    // INTERNAL, but those are actions inside the view, not its entry condition).
    requires: ["payments:read"],
  },
  {
    to: "/app/accounts-wallets",
    path: "accounts-wallets/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_wallets",
    requires: ["operations:public_accounts:read"],
    // public-accounts.controller.ts — every route carries userTypes: [INTERNAL].
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payins",
    path: "payins/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_payins",
    // payins.internal.controller.ts:107 — payments:read, no userTypes.
    requires: ["payments:read"],
  },
  {
    to: "/app/dispersions",
    path: "dispersions/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_payouts",
    // payouts.internal.controller.ts:53 — payments:read to reach the wizard; creating the payout needs
    // payments:write, which the backend enforces on submit.
    requires: ["payments:read"],
  },
  {
    to: "/app/breb-qr-pay",
    path: "breb-qr-pay/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_breb_qr_pay",
    // breb-qr.internal.controller.ts — payments:read to read a QR; paying it needs payments:write, which the
    // COP send panel this entry opens already requires.
    requires: ["payments:read"],
    enabled: (ctx) => ctx.brebQrPaymentsEnabled,
  },
  {
    to: "/app/payout-batches",
    path: "payout-batches/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_payout_batches",
    // payout-batches.internal.controller.ts — every read is payout_batches:read with no userTypes; upload,
    // selection, revalidate and cancel need payout_batches:write; execute, retry and resume need
    // payout_batches:approve:write plus 2FA, and never on the uploader's own batch. The screens hide what the
    // principal cannot do (utils/batchPermissions.ts); the backend enforces it per request. Members of an
    // organization staff has not enabled get 403 `payout_batches_not_enabled` on every route, so the flag hides
    // the entry and PayoutBatchesNavigation closes the route.
    requires: ["payout_batches:read"],
    enabled: (ctx) => ctx.payoutBatchesEnabled,
  },
  {
    to: "/app/payments-activity",
    path: "payments-activity/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_payments_activity",
    requires: ["payments:read"],
  },
  {
    // WhatsApp alerts for money in and out: collections, payouts, batches and Bre-B. Reading the preferences is
    // `payments:read`; each switch PATCHes with `payments:write`, which the backend enforces. Members only, for
    // the same reason as the cards one: the page configures the caller's OWN organization, with no picker.
    to: "/app/wallets/notifications",
    path: "wallets/notifications",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_payments_notifications",
    requires: ["payments:read"],
    userTypes: [UserType.ORGANIZATION_MEMBER],
  },
  // Members of an organization without Crypto get 403 `otc_not_enabled` on quotes and orders, so the flag
  // hides buying and selling and CryptoSwapsNavigation closes those routes; history and details stay.
  // `crypto-swaps/*` is ONE mounted route serving five surfaces (buy and sell forms, history, one order,
  // one sale), so the route carries `RequireRouteAccess` and each sub-route its own `RequireAccess`, the
  // `cards/*` pattern.
  // otc.internal.controller.ts — reads are payments:read and quote/order writes payments:write, with NO
  // userTypes: members are pinned to their own organization and staff pass `organization_id`. The buy
  // form gates its submit on `can("payments:write")`.
  {
    to: "/app/crypto-swaps",
    path: "crypto-swaps/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_crypto_swaps",
    requires: ["payments:read"],
    enabled: cryptoEnabled,
    menuGroup: "crypto-swaps",
  },
  {
    // otc-sell.internal.controller.ts — the purchase's contract: payments:read to read, payments:write to
    // quote and place, no userTypes. The sell form gates its submit on `can("payments:write")`.
    to: "/app/crypto-swaps/sell",
    path: "crypto-swaps/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_crypto_sell",
    requires: ["payments:read"],
    enabled: cryptoEnabled,
    menuGroup: "crypto-swaps",
  },
  {
    to: "/app/crypto-swaps/history",
    path: "crypto-swaps/*",
    section: "home",
    placement: "sidebar",
    labelKey: "sidebar_menu_crypto_swaps_history",
    requires: ["payments:read"],
    // An order or a sale is a record, so the history owns it in the menu.
    activeFor: ["/app/crypto-swaps/orders/:orderId", "/app/crypto-swaps/sales/:saleId"],
    menuGroup: "crypto-swaps",
  },
  {
    to: "/app/crypto-swaps/orders/:orderId",
    path: "crypto-swaps/*",
    section: "home",
    placement: "route",
    labelKey: null,
    requires: ["payments:read"],
  },
  {
    to: "/app/crypto-swaps/sales/:saleId",
    path: "crypto-swaps/*",
    section: "home",
    placement: "route",
    labelKey: null,
    requires: ["payments:read"],
  },
  {
    // Organization KYB. No `requires` on purpose: it is the organization verifying ITSELF, reached from
    // the Wallets banner, and gating it behind `identities:read` would strand a member who has to
    // complete it. The engine authorizes the flow by its own session token.
    to: "/app/onboarding",
    path: "onboarding/*",
    section: "home",
    placement: "route",
    labelKey: null,
  },

  // ── cards ─────────────────────────────────────────────────────────────────────────────────────
  //
  // `cards/*` is ONE mounted route serving several surfaces, and they do NOT share a requirement:
  // holders read the identities module and pockets read the wallets module. The three entries below
  // therefore carry different `requires`, the parent route opens when ANY of them is reachable, and
  // `CardsNavigation` guards each sub-route with its own entry. Collapsing them onto `cards:read` —
  // which is what the single `AuthorizationBoundary` on the whole route used to do — showed the menu
  // items to anyone holding `cards:read` alone and let the API answer 401.
  {
    to: "/app/cards/cards",
    path: "cards/*",
    section: "cards",
    placement: "sidebar",
    labelKey: "sidebar_menu_cards",
    // cards.internal.controller.ts:105 — /internal/cards.
    requires: ["cards:read"],
  },
  {
    to: "/app/cards/identities",
    path: "cards/*",
    section: "cards",
    placement: "sidebar",
    labelKey: "sidebar_menu_holders",
    // identities.internal.controller.ts:66 — the page reads /internal/identities, not /internal/cards.
    requires: ["identities:read"],
  },
  {
    to: "/app/cards/pockets",
    path: "cards/*",
    section: "cards",
    placement: "sidebar",
    labelKey: "sidebar_menu_pockets",
    // pockets.internal.controller.ts:139 — /internal/pockets authorizes on the wallets module.
    requires: ["wallets:read"],
    enabled: (ctx) => ctx.pocketsVisible,
  },
  {
    // Printable statement. Lives outside DashboardLayout (ColocaAppShell clips the viewport and Chrome
    // will not paginate through that), so it is a route-only entry with the same requirement as cards.
    to: "/app/cards/:id/statement",
    path: "cards/:id/statement",
    section: "cards",
    placement: "route",
    labelKey: null,
    requires: ["cards:read"],
  },
  {
    // Client organizations, the INTERNAL landing inside the cards module. Its requirement already lived
    // in `CardsNavigation` as an inline AuthorizationBoundary; declaring it here is what lets the route
    // guard and the rest of the module share one source.
    to: "/app/cards/organizations",
    path: "cards/organizations",
    section: "cards",
    placement: "route",
    labelKey: null,
    // organizations.internal.controller.ts:127,157,178.
    requires: ["organizations:read"],
  },
  {
    // Card-wallet statement, reached from the balance banner. It had no guard of its own and inherited
    // `cards:read` from the parent, while all three calls it makes authorize on the wallets module.
    to: "/app/cards/movements",
    path: "cards/movements",
    section: "cards",
    placement: "route",
    labelKey: null,
    // wallets.internal.controller.ts:185, :252, :420.
    requires: ["wallets:read"],
  },
  {
    // Card design. Reading the styles is `cards:read` (organizations.internal.controller.ts:300); the
    // mutations behind it are `cards:write` and the backend additionally gates them on the
    // organization's `can_customize_card_design` flag, so the write affordance is not this entry's job.
    to: "/app/cards/branding",
    path: "cards/branding",
    section: "cards",
    placement: "route",
    labelKey: null,
    requires: ["cards:read"],
  },
  {
    // WhatsApp alerts to the cardholder. Reading the preferences is `cards:read`
    // (organizations.internal.controller.ts:430); the PATCH behind each switch is `cards:write`, which the
    // backend enforces — not this entry's job.
    //
    // `userTypes` is NOT read off the endpoint here, which declares none: the page configures the preferences
    // of the caller's OWN organization (`user.organization_id`) and there is no organization picker, so for
    // staff — who have none — every switch would be inert. This is the same tenancy fact the Settings page
    // encoded by hiding the section from internal users; closing the route states it once instead.
    to: "/app/cards/notifications",
    path: "cards/notifications",
    section: "cards",
    placement: "sidebar",
    labelKey: "sidebar_menu_cards_notifications",
    requires: ["cards:read"],
    userTypes: [UserType.ORGANIZATION_MEMBER],
  },

  // ── analytics ─────────────────────────────────────────────────────────────────────────────────
  {
    to: "/app/audit",
    path: "audit/*",
    section: "analytics",
    placement: "sidebar",
    labelKey: "sidebar_menu_audit",
    requires: ["operations:audit:read"],
    // audit.controller.ts — every route carries userTypes: [INTERNAL]. This matters: an invited member
    // DOES hold operations:audit:read (it is in MEMBER_DEFAULT_SCOPES), so without this axis the entry
    // would show and every request behind it would 403.
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/accounts-wallets-tracking",
    path: "accounts-wallets-tracking/*",
    section: "analytics",
    placement: "sidebar",
    labelKey: "sidebar_menu_accounts_tracking",
    requires: ["operations:public_accounts:read"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/metrics",
    path: "metrics/*",
    section: "analytics",
    placement: "sidebar",
    labelKey: "sidebar_menu_metrics",
    // metrics.internal.controller.ts and experiments.internal.controller.ts — reads are payments:read +
    // userTypes: [INTERNAL]; changing an experiment is organizations:write, gated in the page.
    requires: ["payments:read"],
    userTypes: [UserType.INTERNAL],
    enabled: (ctx) => ctx.metricsAllowed,
  },
  {
    // Read-only forensic surface: the journal entries behind every runtime operation. It sits with the
    // other audit tools rather than under payments-config, which is for what gets edited.
    to: "/app/payments-ledger",
    path: "payments-ledger/*",
    section: "analytics",
    placement: "sidebar",
    labelKey: "sidebar_menu_payments_ledger",
    requires: ["payments:read"],
    // payment-ledger.internal.controller.ts:16 — payments:read + userTypes: [INTERNAL]. Same pair as
    // payments-config; the status-changes endpoint the trail reads carries the identical guard.
    userTypes: [UserType.INTERNAL],
  },
  {
    // Staff surface for the organization-verification gate: read one organization's own KYC/KYB and lift
    // its identity-change cooldown when a member asked for an early change (Settings → Verification).
    to: "/app/account-verification",
    path: "account-verification/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_account_verification",
    requires: ["identities:write"],
    // organization-verification.internal.controller.ts — identity-change-waiver is identities:write +
    // userTypes: [INTERNAL]; the read endpoint accepts any JWT, so the write pair is the one mirrored here.
    userTypes: [UserType.INTERNAL],
  },

  // ── management ────────────────────────────────────────────────────────────────────────────────
  {
    to: "/app/organizations",
    path: "organizations/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_organizations",
    // organizations.internal.controller.ts — the list is organizations:read + userTypes: [INTERNAL]; the
    // feature switches (PATCH :id/features) are organizations:write + INTERNAL, gated in the page.
    requires: ["organizations:read"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/wallet-top-ups",
    path: "wallet-top-ups/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_wallet_top_ups",
    // wallet-top-ups.internal.controller.ts — list/access are payments:read + userTypes: [INTERNAL], create is
    // payments:write + INTERNAL + 2FA, and every one of them also requires the email allowlist, which only the
    // backend knows: `enabled` hides the entry from staff off it, and the navigation closes the route.
    requires: ["payments:read"],
    userTypes: [UserType.INTERNAL],
    enabled: (ctx) => ctx.walletTopUpsAllowed,
  },
  {
    to: "/app/card-wallets",
    path: "card-wallets",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_card_wallets",
    // wallets.internal.controller.ts — `POST :id/adjust-balance` is wallets:write + userTypes: [INTERNAL].
    // The reads behind the screen (the balance, the statement, the organizations picker) are wallets:read and
    // organizations:read, which a wallets:write holder has in practice; `requires` is ANY-of, so listing them
    // would LOOSEN the gate rather than tighten it. The screen exists to write — its read-only twin is
    // /app/cards/movements, which already carries wallets:read.
    requires: ["wallets:write"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/routes-countries",
    path: "routes-countries/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_routes_countries",
    // No `requires`: it renders ComingSoonPage and calls no endpoint. INTERNAL-only so a placeholder
    // never reaches an organization.
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payments-config/rails",
    path: "payments-config/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_payment_rails",
    requires: ["payments:read"],
    // payment-rails.internal.controller.ts — payments:read + userTypes: [INTERNAL].
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payments-config/limits",
    path: "payments-config/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_payment_limits",
    requires: ["payments:read"],
    // payment-limits.internal.controller.ts — payments:read + userTypes: [INTERNAL].
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payments-config/routes",
    path: "payments-config/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_payment_routes",
    requires: ["payments:read"],
    // payment-config.internal.controller.ts:50,89 — payments:read + userTypes: [INTERNAL].
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payments-config/fees",
    path: "payments-config/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_payment_fees",
    requires: ["payments:read"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payments-config/taxes",
    path: "payments-config/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_payment_taxes",
    requires: ["payments:read"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payments-config/rate-providers",
    path: "payments-config/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_rate_providers",
    requires: ["payments:read"],
    // rates.internal.controller.ts — the provider-usage route carries payments:read + userTypes:
    // [INTERNAL]. Staff-only matters here beyond tenancy: the view renders provider codes verbatim.
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payments-config/deposit-clabes",
    path: "payments-config/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_deposit_clabes",
    requires: ["payments:read"],
    // bitso-clabes.internal.controller.ts:46 — payments:read + userTypes: [INTERNAL].
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/payments-config/deposit-cvus",
    path: "payments-config/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_deposit_cvus",
    requires: ["payments:read"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/quoter",
    path: "quoter/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_quoter",
    // shipping-quotes.internal.controller.ts:58 — operations:shipping_quotes:read for the history.
    requires: ["operations:shipping_quotes:read"],
    // The quote itself comes from `GET /internal/payments/rates`, whose bare @Get() is
    // userTypes: [INTERNAL] (rates.internal.controller.ts:28) — so the tool is internal end to end.
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/quoter-coloca",
    path: "quoter-coloca/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_quoter_coloca",
    // Renders the same QuoterForm, so its requirement is the rates endpoint, not shipping-quotes.
    requires: ["payments:read"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/users",
    path: "users/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_users",
    requires: ["users:read"],
    // PRODUCT: users.internal.controller.ts declares no userTypes, but organizations manage their own
    // people from Settings → Access & permissions. Keeping this internal preserves today's menu.
    internalOnlyMenu: true,
  },
  /*
   * The access catalog: roles, policies and API keys as ENTITIES. Assigning a role to one person stays in
   * `/app/users` — the backend only links roles to principals, never policies or loose permissions, so the
   * two surfaces cannot overlap.
   *
   * All three share `path: "access/*"` because one mounted route serves them, exactly like payments-config.
   * They are `internalOnlyMenu` and NOT `userTypes: [INTERNAL]`: roles.controller.ts:48,
   * policies.controller.ts:49 and api-keys.controller.ts:66 declare permissions only — no tenancy — and an
   * org admin holds `roles:*` / `policies:*` by default. Declaring tenancy here would misdescribe the API.
   * Organizations administer the same catalog from Settings → Access & permissions, which is why hiding the
   * menu entry loses them nothing. Same reasoning, same wording, as `/app/users` above.
   */
  {
    to: "/app/access/roles",
    path: "access/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_roles",
    requires: ["roles:read"],
    internalOnlyMenu: true,
  },
  {
    to: "/app/access/policies",
    path: "access/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_policies",
    requires: ["policies:read"],
    internalOnlyMenu: true,
  },
  {
    to: "/app/access/api-keys",
    path: "access/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_api_keys",
    requires: ["api_key:read"],
    internalOnlyMenu: true,
  },
  {
    to: "/app/clients",
    path: "clients/*",
    section: "management",
    placement: "sidebar",
    labelKey: "sidebar_menu_clients",
    // clients.internal.controller.ts:91,125 — clients:read, no userTypes.
    requires: ["clients:read"],
  },

  // ── footer & utility surfaces ─────────────────────────────────────────────────────────────────
  {
    to: "/app/settings",
    path: "settings/*",
    section: null,
    placement: "footer",
    labelKey: "sidebar_settings_label",
  },
  {
    to: "/app/news",
    path: "news/*",
    section: null,
    placement: "footer",
    labelKey: "sidebar_menu_news",
    // notes.controller.ts:100,114 — operations:notes:read + userTypes: [INTERNAL]. The route used to be
    // ungated while the menu item was hidden, so an org member redirected here saw internal notes.
    requires: ["operations:notes:read"],
    userTypes: [UserType.INTERNAL],
  },
  {
    to: "/app/profile",
    path: "profile/*",
    section: null,
    placement: "route",
    labelKey: null,
  },
] as const;

/** Sidebar section titles, keyed by section id. */
export const SECTION_TITLE_KEYS: Record<SectionId, keyof GlobalTranslations> = {
  operations: "sidebar_section_operaciones",
  home: "sidebar_section_billeteras",
  cards: "sidebar_section_tarjetas",
  analytics: "sidebar_section_analitica",
  management: "sidebar_section_gestion",
};
